import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { NewHolding, TickerPosition } from "@bloomberg/octagon-shared";
import { Change, Variant } from "@bloomberg/media-ui-markets";
import { PrimaryButton } from "@bloomberg/media-ui-core";
import styles from "./styles.module.css";
import RenderSparkLine from "./RenderSparkLine";
import PositionsTableSkeleton from "./PositionsTableSkeleton";
import SecurityTypeFilter from "~components/Watchlist/delta/Filters/SecurityTypeFilter";
import SectorsFilter from "~components/Watchlist/delta/Filters/SectorsFilter";
import BulkEditModal, {
  type BulkEditMetadata,
} from "~components/Watchlist/delta/BulkEdit";
import type { HoldingRowState } from "~components/Watchlist/delta/BulkEdit/HoldingsRow";
import Table from "~components/Table";
import TableHeader from "~components/Table/TableHeader";
import TableColumn from "~components/Table/TableColumn";
import TableBody from "~components/Table/TableBody";
import TableRow from "~components/Table/TableRow";
import TableCell from "~components/Table/TableCell";
import {
  useCurrentWatchlist,
  useError,
  useFilteredPositions,
  useIsLoading,
  useSelectedPortfolioId,
  useWatchlistActions,
} from "~context/WatchlistContext";
import type { TableColumnDefinition } from "~types/table";
import { formatBigNumber } from "~utils/portfolio/tickerUtils";
import { formatWithCommasAndDecimals } from "~utils/portfolio/formatNumber";
import PriceRangeBar from "~components/Watchlist/delta/PriceRangeBar";
import timeFormat from "~utils/timeFormat";
import { Locale } from "~constants/i18n";
import TickerSearch from "~components/Watchlist/delta/TickerSearch";
import TableErrorState from "~components/Watchlist/delta/TableErrorState";
import HoldingsDrawer from "~components/Watchlist/delta/Drawer/HoldingsDrawer";

// ─── Row type ────────────────────────────────────────────────────────────────

type PositionTableRow = {
  id: string;
  ticker: string;
  name: string;
  securityType: string;
  // Portfolio data
  shares: number;
  totalValue: number | null;
  avgCost: number | null;
  // Gain/loss
  totalGainLoss: number | null;
  totalGainLossPercent: number | null;
  todayGainLoss: number | null;
  todayGainLossPercent: number | null;
  // Market data
  price: number | null;
  priceChange1Day: number | null;
  openPrice: number | null;
  prevClose: number | null;
  volume: number | null;
  marketCap: number | null;
  highPrice: number | null;
  lowPrice: number | null;
  highPrice52Week: number | null;
  lowPrice52Week: number | null;
  lastUpdate: string | null;
  // Trend sparkline
  priceMovements: number[];
  changePercent: number | null;
};

// ─── Adapters ─────────────────────────────────────────────────────────────────

function positionToRow(position: TickerPosition): PositionTableRow {
  const securityType =
    position.marketData.securityType ??
    position.marketData.mediaSecurityType ??
    "";
  const shares = position.lots.reduce((sum, l) => sum + l.shares, 0);

  return {
    id: position.ticker,
    ticker: position.ticker,
    name: position.marketData.longName,
    securityType,
    shares,
    totalValue: position.marketValue.value ?? null,
    avgCost: position.marketValue.averageBuyPrice ?? null,
    totalGainLoss: position.marketValue.totalGainLoss,
    totalGainLossPercent: position.marketValue.totalGainLossPercentChange,
    todayGainLoss: position.marketValue.todayGainLoss,
    todayGainLossPercent: position.marketValue.todayGainLossPercentChange,
    price: position.marketData.price ?? null,
    priceChange1Day: position.marketData.priceChange1Day ?? null,
    openPrice: position.marketData.openPrice ?? null,
    prevClose: position.marketData.previousClosingPriceOneTradingDayAgo ?? null,
    volume: position.marketData.volume ?? null,
    marketCap: position.marketData.marketCap ?? null,
    highPrice: position.marketData.highPrice ?? null,
    lowPrice: position.marketData.lowPrice ?? null,
    highPrice52Week: position.marketData.highPrice52Week ?? null,
    lowPrice52Week: position.marketData.lowPrice52Week ?? null,
    lastUpdate: position.marketData.lastUpdate ?? null,
    priceMovements:
      position.marketData.priceMovements?.map((p) => p.value) ?? [],
    changePercent: position.marketData.percentChange1Day ?? null,
  };
}

// ─── Cell renderers ───────────────────────────────────────────────────────────

function renderSymbol(row: PositionTableRow) {
  return (
    <div className={styles.symbolCell}>
      <a
        href={`/quote/${row.ticker}`}
        className={styles.tickerLink}
        target="_blank"
        rel="noopener noreferrer"
      >
        {row.ticker}
      </a>
      <span className={styles.secondary}>{row.name}</span>
    </div>
  );
}

function renderShares(row: PositionTableRow) {
  if (row.shares === 0) return <span className={styles.tertiary}>—</span>;

  return <span className={styles.primary}>{row.shares}</span>;
}

function formatPrice(price: number, securityType: string): string {
  if (securityType === "CUR") {
    return String(formatWithCommasAndDecimals(price, 4));
  }

  return String(formatWithCommasAndDecimals(price));
}

function renderTotalValue(row: PositionTableRow) {
  if (row.totalValue === null)
    return <span className={styles.tertiary}>—</span>;

  return (
    <span className={styles.primary}>
      {formatPrice(row.totalValue, row.securityType)}
    </span>
  );
}

function renderAvgCost(row: PositionTableRow) {
  if (row.avgCost === null) return <span className={styles.tertiary}>—</span>;

  return (
    <span className={styles.primary}>
      {formatPrice(row.avgCost, row.securityType)}
    </span>
  );
}

function renderGain(value: number | null, pct: number | null) {
  if (value === null) return <span className={styles.tertiary}>—</span>;

  const absFormatted = formatWithCommasAndDecimals(Math.abs(value));
  const absLabel = `${value >= 0 ? "+" : "-"} ${absFormatted}`;

  return (
    <div className={styles.gainCell}>
      <Change percentChange={pct ?? 0} variant={Variant.refresh2024} />
      <span className={styles.gainAbs}>{absLabel}</span>
    </div>
  );
}

function renderBigNumber(value: number | null, securityType: string) {
  if (value === null) return <span className={styles.tertiary}>—</span>;

  const formatted =
    securityType === "CUR"
      ? formatWithCommasAndDecimals(value, 4)
      : formatBigNumber(value, 2);

  return <span className={styles.primary}>{formatted}</span>;
}

function renderOpen(row: PositionTableRow) {
  const formattedTime = row.lastUpdate
    ? timeFormat(row.lastUpdate, Locale.En, "HH:mm")
    : null;

  return (
    <div className={styles.cellStack}>
      {row.openPrice !== null ? (
        <span className={styles.primary}>
          {formatPrice(row.openPrice, row.securityType)}
        </span>
      ) : (
        <span className={styles.tertiary}>—</span>
      )}
      {formattedTime && (
        <span className={styles.secondary}>{formattedTime}</span>
      )}
    </div>
  );
}

function renderPrice(row: PositionTableRow) {
  if (row.price === null) return <span className={styles.tertiary}>—</span>;

  return (
    <div className={styles.cellStack}>
      <span className={styles.primary}>
        {formatPrice(row.price, row.securityType)}
      </span>
      {row.lastUpdate && (
        <span className={styles.secondary}>
          {timeFormat(row.lastUpdate, Locale.En, "HH:mm")}
        </span>
      )}
    </div>
  );
}

function renderChange(row: PositionTableRow) {
  if (row.changePercent === null)
    return <span className={styles.tertiary}>—</span>;

  return (
    <div className={styles.gainCell}>
      <Change percentChange={row.changePercent} variant={Variant.refresh2024} />
      {row.priceChange1Day !== null && (
        <span className={styles.gainAbs}>
          {row.priceChange1Day >= 0 ? "+" : ""}
          {formatPrice(row.priceChange1Day, row.securityType)}
        </span>
      )}
    </div>
  );
}

function renderRange(
  high: number | null,
  low: number | null,
  current: number | null,
  securityType: string
) {
  return (
    <PriceRangeBar
      high={high}
      low={low}
      current={current}
      securityType={securityType}
    />
  );
}

function renderVolume(row: PositionTableRow) {
  if (row.volume === null) return <span className={styles.tertiary}>—</span>;

  return (
    <span className={styles.primary}>
      {formatWithCommasAndDecimals(row.volume, 0)}
    </span>
  );
}

// ─── Column definitions ────────────────────────────────────────────────────────

const LOT_DEPENDENT_KEYS = new Set([
  "shares",
  "totalValue",
  "avgCost",
  "totalGainLoss",
  "todayGainLoss",
]);

const COLUMNS: Array<TableColumnDefinition<PositionTableRow>> = [
  {
    title: "Symbol",
    key: "ticker",
    align: "start",
    allowSorting: true,
    renderer: renderSymbol,
  },
  {
    title: "Shares",
    key: "shares",
    align: "end",
    allowSorting: true,
    renderer: renderShares,
  },
  {
    title: "Total Value",
    key: "totalValue",
    align: "end",
    allowSorting: true,
    renderer: renderTotalValue,
  },
  {
    title: "Avg Cost",
    key: "avgCost",
    align: "end",
    allowSorting: true,
    renderer: renderAvgCost,
  },
  {
    title: "Price",
    key: "price",
    align: "end",
    allowSorting: true,
    renderer: renderPrice,
  },
  {
    title: "Change",
    key: "changePercent",
    align: "end",
    allowSorting: true,
    renderer: renderChange,
  },
  {
    title: "Total Gain",
    key: "totalGainLoss",
    align: "end",
    allowSorting: true,
    renderer: (row) => renderGain(row.totalGainLoss, row.totalGainLossPercent),
  },
  {
    title: "1D Gain",
    key: "todayGainLoss",
    align: "end",
    allowSorting: true,
    renderer: (row) => renderGain(row.todayGainLoss, row.todayGainLossPercent),
  },
  {
    title: "Trend",
    key: "priceMovements",
    align: "end",
    allowSorting: false,
    renderer: (row) => <RenderSparkLine ticker={row.ticker} />,
  },
  {
    title: "Open",
    key: "openPrice",
    align: "end",
    allowSorting: true,
    renderer: renderOpen,
  },
  {
    title: "Prev Close",
    key: "prevClose",
    align: "end",
    allowSorting: true,
    renderer: (row) => renderBigNumber(row.prevClose, row.securityType),
  },
  {
    title: "Vol",
    key: "volume",
    align: "end",
    allowSorting: true,
    renderer: renderVolume,
  },
  {
    title: "Mkt Cap",
    key: "marketCap",
    align: "end",
    allowSorting: true,
    renderer: (row) => renderBigNumber(row.marketCap, row.securityType),
  },
  {
    title: "Day Range",
    key: "highPrice",
    align: "end",
    allowSorting: true,
    renderer: (row) =>
      renderRange(row.highPrice, row.lowPrice, row.price, row.securityType),
  },
  {
    title: "52Wk Range",
    key: "highPrice52Week",
    align: "end",
    allowSorting: true,
    renderer: (row) =>
      renderRange(
        row.highPrice52Week,
        row.lowPrice52Week,
        row.price,
        row.securityType
      ),
  },
];

// ─── Bulk edit helpers ───────────────────────────────────────────────────────

function rowToHolding(ticker: string, row: HoldingRowState): NewHolding {
  return {
    id: /^\d+$/.test(row.id) ? Number.parseInt(row.id, 10) : undefined,
    ticker,
    shares: row.shares !== "" ? Number(row.shares) : undefined,
    buyPrice: row.price !== "" ? Number(row.price) : undefined,
    buyDate: row.date ? new Date(row.date) : undefined,
  };
}

function lotToHolding(position: TickerPosition): NewHolding[] {
  return position.lots.map((lot) => ({
    id: lot.id,
    ticker: position.ticker,
    shares: lot.shares,
    buyPrice: lot.buyPrice,
    buyDate: lot.buyDate,
  }));
}

// ─── Row sorting ──────────────────────────────────────────────────────────────

type SortableKey = keyof PositionTableRow;

function sortRows(
  rows: PositionTableRow[],
  sortBy: SortableKey,
  sortDir: "asc" | "desc"
): PositionTableRow[] {
  return [...rows].sort((a, b) => {
    const aVal = a[sortBy];
    const bVal = b[sortBy];

    // Nulls always last
    if (aVal === null && bVal === null) return 0;
    if (aVal === null) return 1;
    if (bVal === null) return -1;

    let cmp: number;

    if (typeof aVal === "string" && typeof bVal === "string") {
      cmp = aVal.localeCompare(bVal);
    } else if (typeof aVal === "number" && typeof bVal === "number") {
      cmp = aVal - bVal;
    } else {
      cmp = 0;
    }

    return sortDir === "asc" ? cmp : -cmp;
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PositionsTable() {
  const positions = useFilteredPositions();
  const currentWatchlist = useCurrentWatchlist();
  const isLoading = useIsLoading();
  const error = useError();
  const selectedPortfolioId = useSelectedPortfolioId();
  const actions = useWatchlistActions();
  const [sortBy, setSortBy] = useState<SortableKey>("ticker");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);

  useEffect(() => {
    setIsBulkEditOpen(false);
  }, [selectedPortfolioId]);

  const isTableMounted = !isLoading && !error && positions.length > 0;
  const frozenColRef = useRef<HTMLDivElement>(null);
  const scrollColsRef = useRef<HTMLDivElement>(null);
  const leftShadowRef = useRef<HTMLDivElement>(null);
  const rightShadowRef = useRef<HTMLDivElement>(null);

  const syncRowHeights = useCallback(() => {
    const frozenRows = frozenColRef.current?.querySelectorAll("tr");
    const scrollRows = scrollColsRef.current?.querySelectorAll("tr");

    if (!frozenRows || !scrollRows) return;

    // Reset before measuring
    for (const row of [...Array.from(frozenRows), ...Array.from(scrollRows)]) {
      (row as HTMLElement).style.height = "";
    }

    const len = Math.min(frozenRows.length, scrollRows.length);

    for (let i = 0; i < len; i++) {
      const a = frozenRows[i] as HTMLElement;
      const b = scrollRows[i] as HTMLElement;
      const h = Math.max(a.offsetHeight, b.offsetHeight);

      a.style.height = `${h}px`;
      b.style.height = `${h}px`;
    }
  }, []);

  useEffect(() => {
    syncRowHeights();
  }, [positions, sortBy, sortDir, syncRowHeights]);

  useEffect(() => {
    const observer = new ResizeObserver(syncRowHeights);

    if (frozenColRef.current) observer.observe(frozenColRef.current);
    if (scrollColsRef.current) observer.observe(scrollColsRef.current);

    return () => observer.disconnect();
  }, [syncRowHeights, isTableMounted]);

  const updateShadows = useCallback(() => {
    const scrollEl = scrollColsRef.current;

    if (!scrollEl) return;

    const atStart = scrollEl.scrollLeft <= 0;
    const atEnd =
      scrollEl.scrollLeft + scrollEl.clientWidth >= scrollEl.scrollWidth - 1;

    leftShadowRef.current?.classList.toggle(
      styles.scrollShadowVisible,
      !atStart
    );
    rightShadowRef.current?.classList.toggle(
      styles.scrollShadowVisible,
      !atEnd
    );
  }, []);

  useEffect(() => {
    const scrollEl = scrollColsRef.current;

    if (!scrollEl) return undefined;

    updateShadows();
    scrollEl.addEventListener("scroll", updateShadows, { passive: true });
    const observer = new ResizeObserver(updateShadows);

    observer.observe(scrollEl);

    return () => {
      scrollEl.removeEventListener("scroll", updateShadows);
      observer.disconnect();
    };
  }, [positions, updateShadows]);

  useEffect(() => {
    const frozenEl = frozenColRef.current;
    const scrollEl = scrollColsRef.current;

    if (!frozenEl || !scrollEl) return undefined;

    const getBodyRowIndex = (tr: HTMLElement) => {
      const rows = tr.closest("tbody")?.querySelectorAll("tr");

      return rows ? Array.from(rows).indexOf(tr as HTMLTableRowElement) : -1;
    };

    const setHover = (index: number, on: boolean) => {
      [frozenEl, scrollEl].forEach((container) => {
        const row = container.querySelectorAll("tbody tr")[index] as
          | HTMLElement
          | undefined;

        row?.classList.toggle(styles.rowHovered, on);
      });
    };

    const onEnter = (e: Event) => {
      const tr = (e.target as HTMLElement).closest(
        "tbody tr"
      ) as HTMLElement | null;

      if (tr) setHover(getBodyRowIndex(tr), true);
    };

    const onLeave = (e: Event) => {
      const tr = (e.target as HTMLElement).closest(
        "tbody tr"
      ) as HTMLElement | null;

      if (tr) setHover(getBodyRowIndex(tr), false);
    };

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (target.closest("a, button")) return;

      const tr = target.closest("tbody tr") as HTMLElement | null;

      if (!tr) return;

      const idx = getBodyRowIndex(tr);

      if (idx < 0) return;

      const tickerCell = frozenEl
        .querySelectorAll("tbody tr")
        [idx]?.querySelector("a");
      const href = tickerCell?.getAttribute("href");

      if (href) {
        const ticker = href.replace("/quote/", "");

        actions.openDrawer(ticker);
      }
    };

    [frozenEl, scrollEl].forEach((el) => {
      el.addEventListener("mouseover", onEnter);
      el.addEventListener("mouseout", onLeave);
      el.addEventListener("click", onClick);
    });

    return () => {
      [frozenEl, scrollEl].forEach((el) => {
        el.removeEventListener("mouseover", onEnter);
        el.removeEventListener("mouseout", onLeave);
        el.removeEventListener("click", onClick);
      });
    };
  }, [isTableMounted, actions]);

  const handleEditOpen = useCallback(() => {
    setIsBulkEditOpen(true);
  }, []);

  const handleEditClose = useCallback(() => {
    setIsBulkEditOpen(false);
  }, []);

  const handleSaveChanges = useCallback(
    async (metadata: BulkEditMetadata) => {
      if (!currentWatchlist?.positions) return;

      const holdings: NewHolding[] = [];

      for (const position of currentWatchlist.positions) {
        if (metadata.deletedTickers.has(position.ticker)) continue;

        const editedRows = metadata.holdings[position.ticker];

        if (editedRows) {
          for (const row of editedRows) {
            holdings.push(rowToHolding(position.ticker, row));
          }
        } else {
          holdings.push(...lotToHolding(position));
        }
      }

      await actions.saveWatchlistChanges({
        name: metadata.name,
        currency: metadata.currency,
        isDefault: metadata.isDefault,
        holdings,
      });
      setIsBulkEditOpen(false);
    },
    [currentWatchlist, actions]
  );

  const handleDeleteWatchlist = useCallback(async () => {
    await actions.deleteWatchlist();
    setIsBulkEditOpen(false);
  }, [actions]);

  const handleColumnSort = useCallback(
    (columnKey: SortableKey) => {
      if (sortBy === columnKey) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(columnKey);
        setSortDir("asc");
      }
    },
    [sortBy]
  );

  const hasLots = useMemo(
    () => positions.some((p) => p.lots.some((l) => l.shares > 0)),
    [positions]
  );

  const visibleColumns = useMemo(
    () =>
      hasLots ? COLUMNS : COLUMNS.filter((c) => !LOT_DEPENDENT_KEYS.has(c.key)),
    [hasLots]
  );

  const rows = useMemo(
    () => sortRows(positions.map(positionToRow), sortBy, sortDir),
    [positions, sortBy, sortDir]
  );

  const renderTableColumn = (
    column: TableColumnDefinition<PositionTableRow>
  ) => (
    <TableColumn
      key={column.key}
      column={column}
      sortState={column.allowSorting && sortBy === column.key ? sortDir : null}
      onSort={
        column.allowSorting
          ? () => handleColumnSort(column.key as SortableKey)
          : undefined
      }
    >
      {String(column.title)}
    </TableColumn>
  );

  function renderTableContent() {
    if (isLoading) return <PositionsTableSkeleton />;
    if (error) {
      return (
        <>
          <Table>
            <TableHeader>
              {visibleColumns.map((col) => renderTableColumn(col))}
            </TableHeader>
          </Table>
          <TableErrorState onRefresh={actions.refreshCurrentWatchlist} />
        </>
      );
    }
    if (positions.length === 0) return null;

    const [frozenColumn, ...scrollableColumns] = visibleColumns;

    return (
      <div className={styles.positionsTable}>
        <div className={styles.frozenCol} ref={frozenColRef}>
          <Table>
            <TableHeader>{renderTableColumn(frozenColumn)}</TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  rowData={row}
                  columns={[frozenColumn]}
                  className={styles.clickableRow}
                >
                  <TableCell rowData={row} column={frozenColumn} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className={styles.scrollColsWrapper}>
          <div className={styles.scrollCols} ref={scrollColsRef}>
            <Table>
              <TableHeader>
                {scrollableColumns.map(renderTableColumn)}
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    rowData={row}
                    columns={scrollableColumns}
                    className={styles.clickableRow}
                  >
                    {scrollableColumns.map((column) => (
                      <TableCell
                        key={column.key}
                        rowData={row}
                        column={column}
                      />
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div
            ref={leftShadowRef}
            className={`${styles.scrollShadow} ${styles.scrollShadowLeft}`}
          />
          <div
            ref={rightShadowRef}
            className={`${styles.scrollShadow} ${styles.scrollShadowRight}`}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.tableHeader}>
        <h2 className={styles.tableTitle}>Watchlist</h2>
        <div className={styles.tableActions}>
          <PrimaryButton
            type="button"
            title="Edit"
            className={styles.editButton}
            onClick={handleEditOpen}
          >
            Edit
          </PrimaryButton>
        </div>
      </div>
      <div className={styles.toolbar}>
        <TickerSearch />
        <div className={styles.toolbarFilters}>
          <SecurityTypeFilter />
          <SectorsFilter />
        </div>
      </div>
      {renderTableContent()}
      <BulkEditModal
        open={isBulkEditOpen}
        onClose={handleEditClose}
        onSave={handleSaveChanges}
        onDelete={handleDeleteWatchlist}
        initialName={currentWatchlist?.name ?? ""}
        initialCurrency={currentWatchlist?.currency ?? "USD"}
        initialIsDefault={currentWatchlist?.isDefault ?? false}
        positions={currentWatchlist?.positions ?? []}
      />
      <HoldingsDrawer />
    </>
  );
}
